import React from 'react';
import { motion } from 'framer-motion';
import { Icon, Badge } from '../ui';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const InvoiceMockup = () => (
  <div className="relative w-full h-36 bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-2xl shadow-indigo-500/20 border border-slate-100 p-4 overflow-hidden group hover:shadow-indigo-500/30 transition-all duration-500">
    <div className="flex justify-between items-center mb-3">
      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-md">AT</div>
      <div className="text-xs font-mono text-slate-500 bg-slate-50 px-2 py-1 rounded">#FACT-2026-001</div>
    </div>
    <div className="space-y-2 mb-4">
      <div className="h-2.5 w-2/3 bg-gradient-to-r from-slate-100 to-slate-200 rounded-full animate-pulse" />
      <div className="h-2.5 w-4/5 bg-gradient-to-r from-slate-100 to-slate-200 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
    </div>
    <div className="mt-6 flex justify-between items-end">
      <div className="h-10 w-10 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
        <Icon name="IconCheck" size={20} className="text-white" stroke={3} />
      </div>
      <div className="text-right">
        <div className="text-xs text-slate-500">Total TTC</div>
        <div className="text-lg font-bold text-slate-800">1,250.00 TND</div>
      </div>
    </div>
  </div>
);

const XmlMockup = () => (
  <div className="w-full bg-gradient-to-br from-slate-900 to-gray-900 rounded-xl p-4 border border-slate-700/50 font-mono text-xs leading-4 text-slate-300 shadow-xl overflow-hidden group hover:border-indigo-500/30 transition-all duration-300">
    <div className="text-purple-400 flex items-center"><span className="w-2 h-2 bg-purple-500 rounded-full mr-2" />&lt;RetenueSource&gt;</div>
    <div className="pl-4 ml-2 border-l border-slate-700/50 my-2">
      <div className="text-blue-400 flex items-center"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2" />&lt;Ref&gt;<span className="text-slate-200 ml-1">2026-M01</span>&lt;/Ref&gt;</div>
      <div className="text-blue-400 flex items-center mt-1"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2" />&lt;Montant&gt;<span className="text-slate-200 ml-1">500.000</span>&lt;/Montant&gt;</div>
    </div>
    <div className="text-purple-400 flex items-center"><span className="w-2 h-2 bg-purple-500 rounded-full mr-2" />&lt;/RetenueSource&gt;</div>
    <div className="mt-4 flex items-center gap-2 text-green-400 bg-green-900/20 px-3 py-2 rounded-lg border border-green-800/30">
      <Icon name="IconCheckCircle" size={16} />
      <span className="font-semibold">Valide TEJ • Certifié</span>
    </div>
  </div>
);

const ChartMockup = () => {
  const [hoveredBar, setHoveredBar] = React.useState(null);
  const bars = [
    { height: '40%', color: 'from-indigo-400 to-indigo-500', value: '42K' },
    { height: '60%', color: 'from-indigo-500 to-blue-500', value: '68K' },
    { height: '30%', color: 'from-indigo-400 to-indigo-500', value: '25K' },
    { height: '80%', color: 'from-blue-500 to-cyan-500', value: '92K' },
    { height: '50%', color: 'from-indigo-500 to-blue-500', value: '54K' },
  ];
  return (
    <div className="w-full h-28 flex items-end justify-between gap-2 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl border border-slate-700/30 p-3">
      {bars.map((bar, i) => (
        <div key={i} className="relative w-1/5 flex flex-col items-center"
          onMouseEnter={() => setHoveredBar(i)}
          onMouseLeave={() => setHoveredBar(null)}
        >
          <div className={`w-full rounded-t-lg bg-gradient-to-b ${bar.color} transition-all duration-300 ${hoveredBar === i ? 'shadow-lg shadow-blue-500/30' : ''}`}
            style={{ height: bar.height }}
          >
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-bold text-white bg-slate-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
              {bar.value}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const modules = [
  {
    title: 'Facturation Électronique Pro',
    desc: 'Système de facturation intelligent avec signature XAdES qualifiée, QR Code dynamique et archivage sécurisé conforme aux normes DGII.',
    tags: ['XAdES Sign', 'QR Code DGII', 'Archivage 10 ans', 'API REST'],
    icon: 'IconFileCheck',
    featured: true,
    mockup: <InvoiceMockup />,
  },
  {
    title: 'Module TEJ & Fiscalité',
    desc: 'Génération automatique des fichiers XML pour la plateforme TEJ (Retenue à la source) sans erreur.',
    icon: 'IconCodeXml',
    color: 'from-purple-500/10 to-purple-600/10',
    borderColor: 'border-purple-500/20',
    mockup: <XmlMockup />,
  },
  {
    title: 'Gestion Cabinet',
    desc: "Vue globale sur vos collaborateurs, dossiers clients et l'état d'avancement des déclarations.",
    icon: 'IconUsersGroup',
    color: 'from-emerald-500/10 to-green-600/10',
    borderColor: 'border-emerald-500/20',
  },
  {
    title: 'Trésorerie & Soldes',
    desc: 'Suivi des encaissements, soldes clients et lettrage automatique des règlements.',
    icon: 'IconCoin',
    color: 'from-blue-500/10 to-cyan-600/10',
    borderColor: 'border-blue-500/20',
    mockup: <ChartMockup />,
  },
];

export default function ModulesGrid() {
  return (
    <section id="modules" className="mb-32">
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-100px' }}
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.15 } } }}
        className="text-center mb-16"
      >
        <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mb-4">Modules Premium</motion.h2>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg max-w-2xl mx-auto">Solutions complètes adaptées aux besoins des professionnels</motion.p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {modules.map((mod, i) => (
          <motion.div
            key={mod.title}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-50px' }}
            variants={{
              hidden: { opacity: 0, y: 40 },
              show: { opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } },
            }}
            className={mod.featured
              ? 'md:col-span-2 lg:col-span-3 bg-gradient-to-br from-slate-900/60 via-slate-900/40 to-indigo-900/20 rounded-3xl p-8 border border-slate-700/50 shadow-2xl shadow-indigo-900/30 hover:shadow-indigo-900/50 transition-all duration-500 group relative overflow-hidden'
              : `bg-gradient-to-br ${mod.color} to-slate-900/30 rounded-2xl p-6 border ${mod.borderColor} hover:border-indigo-500/50 transition-all duration-300 group hover:shadow-2xl hover:shadow-indigo-500/10 relative overflow-hidden`
            }
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative z-10">
              {mod.featured ? (
                <div className="flex flex-col lg:flex-row gap-12 items-center">
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-3 mb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-indigo-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center border border-indigo-500/30">
                        <Icon name={mod.icon} size={32} className="text-white" />
                      </div>
                      <Badge variant="info" className="bg-gradient-to-r from-indigo-500/20 to-blue-500/20 text-white border border-indigo-500/30">FLAGSHIP MODULE</Badge>
                    </div>
                    <h3 className="text-4xl font-bold text-white mb-4">{mod.title}</h3>
                    <p className="text-slate-300 text-lg mb-8 leading-relaxed">{mod.desc}</p>
                    <div className="flex flex-wrap gap-3">
                      {mod.tags.map((tag) => (
                        <span key={tag} className="px-4 py-2 bg-slate-800/50 rounded-xl text-sm text-slate-300 border border-slate-700/50">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="w-full lg:w-80 transform lg:rotate-2 transition-transform duration-700 group-hover:rotate-0 group-hover:scale-105">{mod.mockup}</div>
                </div>
              ) : (
                <>
                  <div className="w-12 h-12 bg-gradient-to-br from-white/10 to-white/5 rounded-xl flex items-center justify-center mb-4 border border-white/10">
                    <Icon name={mod.icon} size={24} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{mod.title}</h3>
                  <p className="text-slate-300 text-sm mb-6 leading-relaxed">{mod.desc}</p>
                  {mod.mockup ? (
                    <div className="mt-4">{mod.mockup}</div>
                  ) : (
                    <div className="space-y-3 mt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse" />
                          <span className="text-sm text-slate-300">Déclarations TVA</span>
                        </div>
                        <span className="text-sm font-bold text-white">80%</span>
                      </div>
                      <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full w-4/5" />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
